import { UtilService } from 'src/app/core/services/util/util.service';
import { ApplicationRef, Injectable, OnDestroy, inject, signal } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, ReplaySubject, Subject, Subscription, defer, firstValueFrom, from, fromEvent, fromEventPattern, merge, of } from 'rxjs';
import { defaultIfEmpty, share, shareReplay, switchMap, take, tap, throttleTime } from 'rxjs/operators';
import { BrowserPlatformService } from 'src/app/core/services/browser-platform/browser-platform.service';
import { UserDataService, UserActionType } from './user-data.service';
import { SeriesService } from '../series/series.service';
import { UserSeriesService } from './user-series.service';
import { YeshivaModels } from 'src/serverTypes/yeshivaTypesDeclare';
import { I18nService } from 'src/app/core/services/i18n/i18n.service';
import { HttpClient } from '@angular/common/http';
import { AppGlobalsModule } from '../app-globals.module';
import { environment } from 'src/environments/environment';

type UserBarGlobal = Record<string, unknown>;
type UserBarMethod = (...args: unknown[]) => unknown;
type UserBarWindow = Window & { user?: unknown };

export interface UserSeries {
    series: YeshivaModels.BE.ShiurSeries;
    viewed: number;
    lastUserViewed: number;
}

/** api/auth/passwordreset answers - the reset page switches on it */
export type PasswordResetStatus = 'ok' | 'invalid' | 'expired' | 'used' | 'badPassword' | 'badCaptcha';
export interface PasswordResetState {
    status: PasswordResetStatus;
}

/** api/subscription answers - the /users/unsubscribe page switches on the status */
export type UnsubscribeStatus = 'sent' | 'ok' | 'expired' | 'invalid' | 'badEmail' | 'badService' | 'badCaptcha';
export interface UnsubscribeState {
    status: UnsubscribeStatus;
    /** the normalized service (halacha | shiur | emuna | halachaeng | shiureng | all) */
    service?: string;
    removeAll?: boolean;
    /** confirmation only: the Reg_sub columns switched off */
    cleared?: string[];
}
export interface UnsubscribeRequest {
    email: string;
    serviceType: string;
    /** 'eng' for the English variant of the service (the English mails' links carry it) */
    serviceLanguage: string;
    removeAll: boolean;
    /** the invisible reCAPTCHA response */
    captcha: string;
}

@Injectable({
    providedIn: 'root',
})
export class UserService implements OnDestroy {
    protected userDataService = inject(UserDataService);
    private seriesService = inject(SeriesService);
    private userSeriesService = inject(UserSeriesService);
    private _utils = inject(UtilService);
    private _http = inject(HttpClient);
    private _appRef = inject(ApplicationRef);
    private _i18n = inject(I18nService);
    private _platform = inject(BrowserPlatformService);

    readonly REPLIES_API = '/Users/UserReplies/';
    readonly QUESTION_API = '/Users/UserQuestions/';
    // Keep in sync with the event dispatched by Content/scripts/Yeshiva/UserBar.js.
    private static readonly USER_BAR_READY_EVENT = 'yeshiva:userbar-ready';

    //observable on user login and logout events.
    private _user_info_observable: Observable<any>;
    private readonly _userGlobalReady$ = this._createUserGlobalReadyStream().pipe(
        share({
            connector: () => new ReplaySubject<UserBarGlobal>(1),
            resetOnComplete: true,
            resetOnError: true,
            resetOnRefCountZero: false,
        }),
    );
    public lessonsLinkUrl: string = '';
    public updatedUserData: Subject<any>;
    private _subscriptions: Subscription[] = [];
    // Tracks whether a user is currently logged in. UserBar fires the user callback with {} on logout
    // but does NOT clear its internal/CMS globals, so isInternal()/cmsBaseLink() keep returning stale
    // values until the next full page load - this lets hasInternalAccess() ignore that stale state.
    private _loggedIn = false;
    // The CMS base link exposed as a signal so links built from it stay reactive to login/logout.
    // Empty unless the user currently has internal access (the base link is server-only data), and
    // cleared on logout - consumers read this instead of the non-reactive cmsBaseLink() getter.
    private readonly _cmsManageBaseLink = signal('');
    readonly cmsManageBaseLink = this._cmsManageBaseLink.asReadonly();
    //the raw user object last emitted by UserBar.js (the /Users/UserName json). Read nihul flags
    //off this directly so they work even against an older UserBar.js that lacks the new getters -
    //the fields are always on the json regardless of which UserBar.js version is served.
    private _userData: Record<string, unknown> | null = null;
    //user jsons applied by ANGULAR code (applyUserData - the nihul revive) rather than
    //arriving from a UserBar callback: merged into the same getUser() stream so every
    //subscriber sees them, and crucially independent of the legacy file - Angular's own
    //state must never hinge on which UserBar.js version the browser happens to run
    private readonly _manualUserEmissions$ = new Subject<Record<string, unknown>>();

    constructor() {
        // The `user` global comes from UserBar.js and may not exist while Angular is hydrating.
        this._user_info_observable = this._platform.isBrowser
            ? merge(
                  this._userGlobalReady$.pipe(switchMap((userGlobal) => this._observeUserCallbacks(userGlobal))),
                  this._manualUserEmissions$,
              ).pipe(shareReplay({ bufferSize: 1, refCount: true }))
            : EMPTY;

        this.updatedUserData = new BehaviorSubject<any>(null);
        this._subscriptions.push(
            this.userDataService.invokeUpdateUserData
                .pipe(
                    //update data only if 500 ms passed then last update invoke
                    // debounceTime(500),
                    throttleTime(500), // Not sure why this code used debounce rather than throttle.
                )
                .subscribe(() => {
                    if (this.userConnected()) {
                        //update user data
                        this.getUserDataSummary()
                            .then((data) => {
                                //notify user data for subscribers
                                this.updatedUserData.next(data);
                            })
                            .catch((error) => {
                                //return null value to the observer in order to keep it alive
                                this.updatedUserData.error(error);
                            });
                    } else {
                        //update subscribers that user is logoff
                        //notify null data to subscribers
                        // console.log('not connected');

                        this.updatedUserData.next(null);
                    }
                }),
        );

        //invoke update user data when user is logging in or logoff
        this._subscriptions.push(
            this.getUser().subscribe((user) => {
                this._loggedIn = this._isLoggedInPayload(user);
                //keep the raw user json so the nihul flags can be read off it directly (see _userData)
                this._userData = this._isRecord(user) ? user : null;
                this._cmsManageBaseLink.set(this.hasInternalAccess() ? this.cmsBaseLink() : '');
                this.updateUserData();
                this.setLessonLink();
                //user emissions originate in legacy UserBar callbacks, which run outside
                //Angular's scheduler (zoneless build): signal writes made here and in every
                //other getUser() subscriber don't schedule change detection, so a POST-bootstrap
                //login change (the cms Revive self-heal re-logging an expired site session)
                //stays invisible until something else repaints. Tick once after the emission
                //settles - deferred so ALL subscribers have written first (AGENTS.md UserBar
                //gotchas; same reason sticky-header/header-menu tick after their writes).
                queueMicrotask(() => this._appRef.tick());
            }),
        );

        //the nihul is the login MASTER on EVERY page, not just the nihul-type surfaces:
        //once per page load, a browser that ever held a nihul login (nihulLogonUrl stored)
        //re-checks and revives an expired site login as soon as possible - the reactive
        //consumers (content pencil, header icons, user summary) pick the change up from
        //the getUser() emission. Anonymous browsers have no stored logon url and skip
        //with ZERO requests. Deferred off the bootstrap path.
        if (this._platform.isBrowser) {
            AppGlobalsModule.setTimeoutIsNotServer(() => void this.ensureNihulUser(), 0);
        }
    }

    ngOnDestroy(): void {
        this._utils.unsubscribe(this._subscriptions);
        if (this.updatedUserData) {
            this.updatedUserData.unsubscribe();
        }
    }

    getUser(): Observable<any> {
        //if login - get user object with user info
        //if logout - get empty object
        return this._user_info_observable;
    }

    private get _userGlobal(): UserBarGlobal | null {
        const userGlobal = (this._platform.nativeWindow as UserBarWindow | null)?.user;
        return this._isRecord(userGlobal) ? userGlobal : null;
    }

    private _createUserGlobalReadyStream(): Observable<UserBarGlobal> {
        return defer(() => {
            const existing = this._userGlobal;
            if (existing) {
                return of(existing);
            }

            const win = this._platform.nativeWindow;
            if (!win) {
                return EMPTY;
            }

            return fromEvent(win, UserService.USER_BAR_READY_EVENT).pipe(
                take(1),
                switchMap(() => {
                    const userGlobal = this._userGlobal;
                    return userGlobal ? of(userGlobal) : EMPTY;
                }),
            );
        });
    }

    private _observeUserCallbacks(userGlobal: UserBarGlobal): Observable<any> {
        const addCallback = this._getUserMethod(userGlobal, 'addCallback');
        if (!addCallback) {
            return EMPTY;
        }

        return fromEventPattern((handler) => addCallback.call(userGlobal, handler));
    }

    private _getUserMethod(userGlobal: UserBarGlobal, methodName: string): UserBarMethod | null {
        const method = userGlobal[methodName];
        return typeof method === 'function' ? (method as UserBarMethod) : null;
    }

    private _callUserMethod(methodName: string, ...args: unknown[]): unknown {
        const userGlobal = this._userGlobal;
        if (!userGlobal) {
            return undefined;
        }

        const method = this._getUserMethod(userGlobal, methodName);
        try {
            return method?.call(userGlobal, ...args);
        } catch (e) {
            //most UserBar methods reference closure globals that are assigned only in its
            //DOMContentLoaded handler - between script eval and that handler they throw.
            //undefined routes every caller to its designed not-ready fallback (userReady
            //waits for the ready event, the boolean wrappers return their fallback).
            return undefined;
        }
    }

    private _callUserBoolean(methodName: string, fallback = false): boolean {
        const result = this._callUserMethod(methodName);
        return typeof result === 'boolean' ? result : fallback;
    }

    private _callUserNumber(methodName: string): number | undefined {
        const result = this._callUserMethod(methodName);
        return typeof result === 'number' ? result : undefined;
    }

    private _callUserString(methodName: string): string {
        const result = this._callUserMethod(methodName);
        return typeof result === 'string' ? result : '';
    }

    private _isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    /**
     *
     * @param forceCheck sometimes we don't have the cookie because the user is checked on the hebrew domain
     * @returns
     */
    userReady(forceCheck: boolean = false): Promise<boolean> {
        const readyPromise = this._callUserMethod('whenReadyPromise', forceCheck);
        if (this._isPromise<boolean>(readyPromise)) {
            return readyPromise;
        }

        return firstValueFrom(
            this._userGlobalReady$.pipe(
                take(1),
                switchMap(() => {
                    const lateReadyPromise = this._callUserMethod('whenReadyPromise', forceCheck);
                    return from(this._isPromise<boolean>(lateReadyPromise) ? lateReadyPromise : Promise.resolve(false));
                }),
                defaultIfEmpty(false),
            ),
        );
    }

    private _isPromise<T>(value: unknown): value is Promise<T> {
        return this._isRecord(value) && typeof value['then'] === 'function';
    }

    //**
    // Indicate if user is connected or disconnected.
    // IMPORTANT: Internally it uses the cookie which cause DEBUGGING bug if login into mobile mode
    // and then desktop mode due to user agent change that cause the server to reject the connection.
    // */
    userConnected(): boolean {
        return this._callUserBoolean('isConnected');
    }

    /**
     * Indicate if user has internal access permissions.
     * @returns true if user is internal user
     */
    isInternal(): boolean {
        //json-first like _nihulFlag: the raw user json always carries the field, so the
        //editor gates (content pencil, replies, hofesh limud) work even against a stale
        //UserBar.js - and a revive applied via applyUserData is seen immediately
        const fromData = this._userData?.['isInternal'];
        if (typeof fromData === 'boolean') {
            return fromData;
        }
        return this._callUserBoolean('isInternal');
    }

    /**
     * Whether the user currently has internal (CMS edit) access. Prefer this over isInternal() for
     * gating UI: it also returns false after an in-tab logout, whereas isInternal()/cmsBaseLink() keep
     * reporting stale access until the next full page load (UserBar does not clear those on logout).
     */
    hasInternalAccess(): boolean {
        return this._loggedIn && this.isInternal();
    }

    private _isLoggedInPayload(user: unknown): boolean {
        // UserBar emits {} on logout and a populated object while a user is present, so an empty object
        // means "logged out".
        return !!user && typeof user === 'object' && Object.keys(user).length > 0;
    }

    /** The user is a cms user with any permission at all - may enter the /nihul shell. */
    nihulUser(): boolean {
        return this._nihulFlag('nihulUser');
    }

    /** The user has the new-nihul MIDRASH permission (the lessons area). */
    nihulMidrash(): boolean {
        return this._nihulFlag('nihulMidrash');
    }

    /** The user may access the new-nihul ASK area (the ASK permission or an answering-rabbi account). */
    nihulAsk(): boolean {
        return this._nihulFlag('nihulAsk');
    }

    /**
     * May create a שאלה יזומה: ask staff or an ask-identity rabbi. A midrash-only rabbi keeps
     * the questions area (their queue, via nihulAsk) but not question creation - the server
     * enforces the same (CreateQuestion 403s a rabbi with no ask id).
     */
    nihulCreateQuestion(): boolean {
        return this._nihulFlag('nihulCreateQuestion');
    }

    /** An answering rabbi limited to their own question queue (no midrash/ask/content permission). */
    nihulRabbiOnly(): boolean {
        return this._nihulFlag('nihulRabbiOnly');
    }

    /**
     * The account's rabbi identities (the user json's nihulRabbiIdentities: every central Rabbis
     * row on this login with its per-area ids). Normally one; a rabbi answering under their own
     * name AND an institutional one holds two, and the nihul offers them a switch over this list
     * (NihulRabbiIdentityService). Json only - an older UserBar.js has no getter for it, and an
     * absent field reads as "no choice", which keeps the server-side defaults in charge.
     */
    nihulRabbiIdentities(): { rabbiId: number; askHebId: number; askEngId: number; midrashId: number }[] {
        const fromData = this._userData?.['nihulRabbiIdentities'];
        if (!Array.isArray(fromData)) {
            return [];
        }
        return fromData
            .filter((row): row is Record<string, unknown> => this._isRecord(row))
            .map((row) => ({
                rabbiId: Number(row['rabbiId']) || 0,
                askHebId: Number(row['askHebId']) || 0,
                askEngId: Number(row['askEngId']) || 0,
                midrashId: Number(row['midrashId']) || 0,
            }));
    }

    /** The user has the ADMIN permission - may manage the cms users (/nihul/nihulusers). */
    nihulAdmin(): boolean {
        return this._nihulFlag('nihulAdmin');
    }

    /** The user has the PROMOTIONS permission - the email-lists (promotions) page. */
    nihulPromotions(): boolean {
        return this._nihulFlag('nihulPromotions');
    }

    /** An editing rabbi (RAV_MIDRASH + email matched to their rav account) - edits their OWN shiurim. */
    nihulRavMidrash(): boolean {
        return this._nihulFlag('nihulRavMidrash');
    }

    /**
     * The nihul address for THIS user's header entry link: a rabbi with only their question
     * queue goes straight to /nihul/ask, a rabbi editing only their own lessons to
     * /nihul/midrash, and any richer combination (both rabbi roles, or any staff permission)
     * to the nihul home. Cosmetic like all client gating - every target enforces its own
     * server authorization.
     */
    nihulEntryLink(): string {
        const rabbiOnly = this.nihulRabbiOnly();
        const ravMidrash = this.nihulRavMidrash();
        //nihulRabbiOnly already excludes MIDRASH/ASK/CanEdit but not the rest - a rabbi who is
        //also admin/promotions staff (or both rabbi roles) belongs on the nihul home
        const staff = this.isInternal() || this.nihulMidrash() || this.nihulAdmin() || this.nihulPromotions();
        if (rabbiOnly && !ravMidrash && !staff) {
            return '/nihul/ask';
        }
        //nihulAsk is true for any answering-rabbi account, so a pure midrash rav has it false
        if (ravMidrash && !rabbiOnly && !staff && !this.nihulAsk()) {
            return '/nihul/midrash';
        }
        return '/nihul';
    }

    /**
     * Read a nihul access flag. Prefer the raw user json (always carries the field), and fall
     * back to the matching window.user getter - so access works even if the served UserBar.js
     * predates these getters, as long as the server json has the flag.
     */
    private _nihulFlag(field: string): boolean {
        const fromData = this._userData?.[field];
        if (typeof fromData === 'boolean') {
            return fromData;
        }
        return this._callUserBoolean(field);
    }

    /**
     * The user's chosen main roles (/nihul/profile) as a UserPermissions bitmask - the nihul
     * menu narrows its TOP BAR to these areas. 0 = no choice = the full menu, which is also
     * what an older UserBar.js or a missing field reads as (fail-open: a display preference
     * must never hide anything on its own). Read off the raw json like the nihul flags.
     */
    nihulMainRoles(): number {
        const fromData = this._userData?.['nihulMainRoles'];
        return typeof fromData === 'number' ? fromData : 0;
    }

    /**
     * The DEVELOPER permission - the maintenance tooling (/nihul/settings). ADMIN satisfies it
     * as a superset, so an admin keeps the page without being granted the flag explicitly.
     */
    nihulDeveloper(): boolean {
        return this._nihulFlag('nihulDeveloper');
    }

    /**
     * May manage the daily-halacha schedule (/nihul/halacha) - STRUCTURE or ASK, ADMIN as a
     * superset. Deliberately NOT nihulAsk, which is true for any answering-rabbi account:
     * rabbi-only accounts are excluded from the schedule.
     */
    nihulHalacha(): boolean {
        return this._nihulFlag('nihulHalacha');
    }

    askHebRavId(): number | undefined {
        return this._callUserNumber('askHebRavId');
    }

    askEngRavId(): number | undefined {
        return this._callUserNumber('askEngRavId');
    }
    setLessonLink(): void {
        let linkEditLessons = this.cmsApiLink();
        let link2EditLessons = this._callUserString('cmsContentLink');
        this.lessonsLinkUrl = linkEditLessons + link2EditLessons;
    }

    getLessonsLink(): string {
        return this.lessonsLinkUrl;
    }
    midrashRavId(): number | undefined {
        return this._callUserNumber('midrashRavId');
    }
    cmsBaseLink(): string {
        return this._callUserString('cmsBaseLink');
    }
    cmsApiLink(): string {
        return this._callUserString('cmsApiLink');
    }

    /**
     * Apply a fresh /Users/UserName-shaped json as the current user. Used by the nihul
     * revive (NihulAccessService) after the cms re-asserted an expired site login.
     * ANGULAR-FIRST: the json is emitted straight into the getUser() stream (so
     * _userData, the nihul flags, and every Angular subscriber update no matter what),
     * and only then pushed to the legacy UserBar (settings/getters for the non-Angular
     * chrome) - a UserBar.js without applyUserData is silently skipped by
     * _callUserMethod, and Angular must never hinge on which version the browser runs.
     */
    applyUserData(data: Record<string, unknown>): void {
        this._manualUserEmissions$.next(data);
        this._callUserMethod('applyUserData', data);
    }

    //one revive attempt per page load, shared by every nihul-type surface (the /nihul
    //app, the on-page edit controls, hofesh limud manage, reply moderation) so
    //concurrent checks all wait for the same answer instead of racing it
    private _nihulRevivePromise: Promise<void> | null = null;

    /**
     * The shared admission check for EVERY nihul-type surface: resolves the user's nihul
     * flags, reviving an expired site login from a live cms ticket when needed. A nihul
     * login's SHORT site cookie (AdminCookieMinutes) dies under the sliding cms ticket,
     * and UserBar's cookie short-circuit then answers "logged out" with no server call -
     * for YEARS that left the on-page nihuls (midrash/ask edit, hofesh limud, replies)
     * silently unavailable until a re-login.
     *
     * Anonymous traffic stays on the no-request fast path: without `force`, the server
     * is only asked when this browser held a nihul login at some point (the nihulLogonUrl
     * localStorage entry) - a browser that never did pays nothing. Pass force=true on
     * dedicated nihul pages (/nihul entry) where one POST is always worth it.
     */
    async ensureNihulUser(force = false): Promise<boolean> {
        if (!this._platform.isBrowser) {
            return false;
        }
        //any persisted nihul evidence (a stored cms link from a past real login) makes the
        //server check worth it; a browser with none pays nothing
        const worthAsking = force || !!(this.cmsApiLinkStored() || this.cmsLogonLinkStored());
        await this.userReady(worthAsking);
        if (!this.nihulUser() && worthAsking) {
            this._nihulRevivePromise ??= this._tryNihulRevive();
            await this._nihulRevivePromise;
        }
        return this.nihulUser();
    }

    /**
     * Is there a LIVE cms (nihul) forms session in this browser? The nihul flags above
     * come from db permissions and outlive the cms ticket, so a site-logged-in nihul
     * user can still have a dead cms session - callers probe this and send the user to
     * the cms logon instead of letting every management api call fail.
     */
    async cmsSessionAlive(): Promise<boolean> {
        if (!this._platform.isBrowser) {
            return false;
        }
        const apiLink = this.cmsApiLinkStored();
        if (!apiLink) {
            return false;
        }
        try {
            const response = await fetch(`${environment.nihulDomain}${apiLink}Session/Ping`, { credentials: 'include' });
            return response.ok && (await response.json()) === true;
        } catch {
            return false;
        }
    }

    /**
     * The cms api address. The nihul address is CLASSIFIED - no part of it may appear
     * as a literal in shipped code (not even a derivation token), so the ONLY sources
     * are the live server-revealed value (present while logged in) and the localStorage
     * copy UserBar persisted from that same revealed value at the last real login. No
     * stored address (this browser never held a nihul login) = empty = callers do nothing.
     */
    cmsApiLinkStored(): string {
        const live = this.cmsApiLink();
        if (live) {
            return live;
        }
        return this._storedLink('cmsApiLink');
    }

    /**
     * The cms logon address, same classified-source rules as cmsApiLinkStored: the live
     * server-revealed cmsLogonLink json field first (read json-first like _nihulFlag, so
     * it works regardless of the served UserBar.js version), then the localStorage copy,
     * then the legacy nihulLogonUrl entry (browsers whose last login predates the
     * cmsLogonUrl persistence - it IS a logon address, possibly with a query to strip).
     */
    cmsLogonLinkStored(): string {
        const fromData = this._userData?.['cmsLogonLink'];
        if (typeof fromData === 'string' && fromData) {
            return fromData;
        }
        return this._storedLink('cmsLogonUrl') || this._storedLink('nihulLogonUrl');
    }

    private _storedLink(key: string): string {
        try {
            return localStorage.getItem(key) ?? '';
        } catch {
            return '';
        }
    }

    /**
     * The site answered anonymous, but this browser may still hold a LIVE cms ticket
     * (the nihul is the login MASTER and its sliding ticket outlives the short site
     * cookie). Ask the cms app to re-assert the site login from its own ticket
     * (Session/Revive; works in the cms app's own process, so unlike UserName's decrypt
     * self-heal it needs no shared machineKey) and apply the returned user json -
     * every subscriber, Angular and legacy, updates reactively.
     */
    private async _tryNihulRevive(): Promise<void> {
        if (!(await this.cmsSessionAlive())) {
            return;
        }
        try {
            //GET + no-store: per-user identity json must never come from the browser's http
            //cache, and varnish only caches allowlisted paths (this is not one). A bodyless
            //POST is not an option - the production chain 411s it
            const response = await fetch(`${environment.nihulDomain}${this.cmsApiLinkStored()}Session/Revive`, {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store',
            });
            if (!response.ok) {
                return;
            }
            const revivedUser = await response.json();
            if (revivedUser && revivedUser.Id) {
                this.applyUserData(revivedUser);
            }
        } catch {
            //a dead ticket 302s to the cms logon html (json() throws) - nothing to revive
        }
    }

    cmsDisconnectLink(): string {
        return this._callUserString('cmsDisconnectLink');
    }

    cmsDisconnect() {
        const disconnectLink = this.cmsDisconnectLink();
        if (disconnectLink) {
            this._http.get(disconnectLink).pipe(take(1)).subscribe();
        }
    }

    repliesLink(): string {
        var relinkLink = this.cmsApiLink() + this._callUserString('repliesLink');
        return relinkLink;
    }

    userLogin(): void {
        this._runWhenUserGlobalReady('login');
    }

    userRegister(): void {
        this._runWhenUserGlobalReady('login', null, null, null, '1');
    }

    userLogout(): void {
        this._runWhenUserGlobalReady('logout');
    }

    closeDialog(): void {
        this._runWhenUserGlobalReady('iframe_closed');
    }

    private _runWhenUserGlobalReady(methodName: string, ...args: unknown[]): void {
        if (this._userGlobal) {
            this._callUserMethod(methodName, ...args);
            return;
        }

        this._userGlobalReady$.pipe(take(1)).subscribe(() => this._callUserMethod(methodName, ...args));
    }

    //invoke an update to user after data is change in backends
    public updateUserData(): void {
        this.userDataService.invokeUpdateUserData.next();
    }

    public getUserDataSummaryObservable(): Observable<any> {
        return this.updatedUserData.asObservable();
    }

    async getUserDataSummary(): Promise<any> {
        return this.userDataService.getDataSummary();
    }

    getUserContentHistory() {
        return this.userDataService.getDataField(UserActionType.History).toPromise();
    }

    getUserFavorits() {
        return this.userDataService.getDataField(UserActionType.Favorite).toPromise();
    }

    async getUserSeries() {
        return await this.userSeriesService.getAll();
    }

    getUpdatedSerieses() {
        return Promise.all([this.seriesService.getAllSeries(), this.userSeriesService.getAll()]).then(([allSerieses, userSeries]) => {
            let updatedSerieses: UserSeries[] = [];
            //foreach series in user data take series that the last content isn't in the viewed array
            userSeries &&
                userSeries.forEach((viewed: number[], id: number) => {
                    let seriesInfo = allSerieses?.get(id);
                    //todo: compare last time of user interaction with the series and time of the last lesson in the series
                    if (seriesInfo != null && !viewed.includes(seriesInfo.LastId)) {
                        let seriesData = {
                            series: seriesInfo,
                            viewed: viewed.length,
                            lastUserViewed: viewed[0],
                        };
                        updatedSerieses.push(seriesData);
                    }
                });

            //order by series update time desc
            updatedSerieses = updatedSerieses.sort((sA, sB) => (sA.series.DateUp > sB.series.DateUp ? 1 : -1)).reverse();

            return updatedSerieses;
        });
    }

    getUserSeriesesSummary() {
        return Promise.all([this.seriesService.getAllSeries(), this.userSeriesService.getAll()]).then(([allSerieses, userSeries]) => {
            let updatedSerieses: UserSeries[] = [];
            //foreach series in user data take series that the last content isn't in the viewed array
            userSeries &&
                userSeries.forEach((viewed: number[], id: number) => {
                    let seriesInfo = allSerieses?.get(id);
                    if (seriesInfo) {
                        let seriesData = {
                            series: seriesInfo,
                            viewed: viewed.length,
                            lastUserViewed: viewed[0],
                        };
                        updatedSerieses.push(seriesData);
                    }
                });

            //order by series update time desc
            updatedSerieses = updatedSerieses.sort((sA, sB) => (sA.series.DateUp > sB.series.DateUp ? 1 : -1)).reverse();

            return updatedSerieses;
        });
    }

    getUserReplies(page = 0): Observable<YeshivaModels.BE.ReplyView[]> {
        return this._http.get<YeshivaModels.BE.ReplyView[]>(this.REPLIES_API + page, {
            params: {},
            headers: { noCache: 'true' },
        });
    }

    getUserQuestions(page = 0): Observable<YeshivaModels.BE.Question[]> {
        return this._http.get<YeshivaModels.BE.Question[]>(this.QUESTION_API + page, {
            headers: { noCache: 'true' },
        });
    }

    /**
     * Asks the server to mail a password-reset link. A POST with the fields in the body: the
     * reCAPTCHA token is ~4k characters and used to ride the query string of a GET, which
     * ASP.NET rejects above maxQueryStringLength (every recovery request failed once the
     * tokens grew, 2026-09-09) - and a secret has no business in a url anyway.
     * Register.aspx reads Request(), which covers form fields exactly like query parameters.
     */
    sendPasswordRecovery(email: string, captcha: string): Observable<boolean> {
        // If email is empty for some reason we don't want to send the request
        if (!email) {
            return new Observable<boolean>((observer) => {
                observer.error('Email is empty');
            });
        }
        const now = new Date();
        const fields = {
            what: 'sendPasswordRecovery',
            sendBy: 'email',
            email,
            'g-recaptcha-response': captcha,
            __date: now.getDate() + '/' + now.getMonth() + '/' + now.getFullYear(),
        };
        return this._http.post<boolean>('/reg/Register.aspx', AppGlobalsModule.jsonToForm(fields));
    }

    /** Is a password-reset link still redeemable (the /users/resetpassword page asks before showing its form). */
    getPasswordResetTokenState(token: string): Observable<PasswordResetState> {
        return this._http.get<PasswordResetState>('/api/auth/passwordresettoken', {
            params: { token },
            headers: { noCache: 'true' },
        });
    }

    /** Redeems a password-reset link for a new password (single use; the server also burns the user's other open links). */
    resetPassword(token: string, password: string, captcha: string): Observable<PasswordResetState> {
        return this._http.post<PasswordResetState>('/api/auth/passwordreset', { token, password, captcha });
    }

    /** Asks the server to mail the unsubscribe confirmation link (nothing changes until it is clicked). */
    requestUnsubscribe(request: UnsubscribeRequest): Observable<UnsubscribeState> {
        return this._http.post<UnsubscribeState>('/api/subscription/unsubscribe', request);
    }

    /** The optional "why are you leaving" note, mailed to the subscriptions inbox. */
    sendUnsubscribeReason(request: UnsubscribeRequest & { reason: string }): Observable<UnsubscribeState> {
        return this._http.post<UnsubscribeState>('/api/subscription/unsubscribereason', request);
    }

    /** Redeems the mailed unsubscribe link: clears the mailing flags it names. */
    confirmUnsubscribe(token: string): Observable<UnsubscribeState> {
        return this._http.post<UnsubscribeState>('/api/subscription/unsubscribeconfirm', { token });
    }

    updateRegistration(userData: any, captcha: string): Observable<string> {
        let jsonData = { act: 'update', 'g-recaptcha-response': captcha, ...userData };

        return this._http.post('/Users/Details/', AppGlobalsModule.jsonToForm(jsonData), {
            responseType: 'text',
        });
    }

    getUserData(): Observable<any> {
        return this._http.get('/Users/UserName?act=userDataJson', {
            responseType: 'json',
            headers: { noCache: 'true' },
        });
    }

    removeRegistration() {
        // http://he.local/Users/Details/
        // userID: 29703
        // act: remove
    }

    getRegistrationCountries(): Observable<Array<string>> {
        let lang = this._i18n.getLangshort();
        return this._http.get<Array<string>>(`/reg/Register.aspx?what=countriesJson&lan=${lang}`).pipe(take(1));
    }

    getRegistrationStates(): Observable<Array<string>> {
        let lang = this._i18n.getLangshort();
        return this._http.get<Array<string>>(`/reg/Register.aspx?what=statesJson&lan=${lang}`).pipe(take(1));
    }

    removeQuestion(id: number) {
        return this._http.post(`/Users/RemoveUserQuestion/${id}`, { _v: 1 }).pipe(tap(() => this.reduceQuestionsAmount()));
    }

    reduceQuestionsAmount() {
        this.getUserDataSummaryObservable()
            // Must happens once otherwise it will be infinie recursion
            .pipe(take(1))
            .subscribe({
                next: (data) => {
                    //notify user data for subscribers
                    data.server.all_answers -= 1;

                    this.updatedUserData.next(data);
                },
            });
        // An alternative we could have retake user data from server but it's redurant request
    }

    registerView(id: number): Observable<string> {
        return this._http.get(`/General/LogView/3/${id}`, { responseType: 'text' });
    }

    /**
     * Register new user via API
     *
     * @param email Email
     * @param password Password
     * @param fname First name
     * @param sname Last name
     * @param captchaToken Google's captcha token
     */
    async addUser(email: string, password: string, fname: string, sname: string, captchaToken: string, comment: string = '') {
        //console.log('add user request');
        let myHeaders = { 'g-recaptcha-response': captchaToken };
        //The varnish replaces the referer-fake to Referer
        this._subscriptions.push(
            this._http
                .post<string>(
                    '/users/registerajax',
                    {
                        e: email,
                        p: password,
                        fn: fname,
                        sn: sname,
                        s: '',
                        po: true,
                        cp: '',
                        captchaId: captchaToken,
                        comment: comment,
                    },
                    { headers: myHeaders },
                )
                .subscribe((result) => console.log('result: ' + JSON.stringify(result['message' as any]))),
        );
    }
}
